import { create } from "zustand";
import { API, instance } from "@/services/API";

// Best-effort message extraction from the various error shapes our API helpers reject
// with. Plain strings (server response.data), Error instances, and axios errors all
// flow through here. Falls back to `fallback` when nothing useful is available.
const formatApiError = (error, fallback) => {
  if (!error) return fallback;
  if (typeof error === "string") return error.trim() || fallback;
  if (error.message && typeof error.message === "string") return error.message;
  if (error.response?.data) {
    const data = error.response.data;
    if (typeof data === "string" && data.trim()) return data;
    if (data.message) return data.message;
  }
  if (error.response?.status) return `Request failed with status ${error.response.status}`;
  return fallback;
};

// Resolves to the response data, or null on 404. Other errors are rejected with the
// same shape as API.get's handleError (response.data || error.message).
const fetchAllowing404 = async (url, config = {}) => {
  try {
    const response = await instance.get(url, {
      ...config,
      validateStatus: (status) => (status >= 200 && status < 300) || status === 404,
    });
    return response.status === 404 ? null : response.data;
  } catch (error) {
    return Promise.reject(error.response?.data || error.message);
  }
};

const initialState = {
  // Trucks
  trucks: { data: null, loading: false, error: null },
  // All trucks belonging to the caller's company (includes the caller's own trucks)
  companyTrucks: { data: null, loading: false, error: null },
  // Truck filters (filters)
  truckFilters: { data: null, loading: false, error: null },
  // Operation states
  createTruckFilterState: { loading: false, error: null },
  deleteTruckState: { loading: false, error: null },
  updateTruckState: { loading: false, error: null },
  deleteTruckFilterState: { loading: false, error: null },
  updateTruckFilterState: { loading: false, error: null },
  // Notification tracking
  unreadLoads: {},
  newLoadTimestamps: {},
  notificationsDisabled: {},
  seenLoadIds: {},
  // Strategy
  strategy: {
    globalText: null, // string | null — saved global text
    truckText: {}, // { [truckId]: string | null } — saved per-truck text
    globalPrefill: {}, // { [truckId]: PostTruckPrefill | null } — global prefill resolved for that truck
    globalPrefillLoaded: {}, // { [truckId]: true } — global prefill fetch completed for that truck (incl. 404)
    truckPrefill: {}, // { [truckId]: PostTruckPrefill } — raw saved per-truck prefill (un-merged)
    truckPrefillLoaded: {}, // { [truckId]: true } — per-truck prefill fetch completed (incl. 404)
    fetchTextState: { loading: false, error: null },
    fetchPrefillState: { loading: false, error: null },
    saveState: { loading: false, error: null },
    extractPreviewGlobal: null, // PostTruckPrefill | null — live modal preview, global scope
    extractPreviewTruck: null, // PostTruckPrefill | null — live modal preview, truck scope
    extractState: { loading: false, error: null },
  },
  dailyCheckIn: { lastCheckInDate: null, fetched: false, loading: false, error: null },
};

export const useLoadFinderStore = create((set, get) => ({
  ...initialState,

  // === TRUCKS ===
  fetchTrucks: async () => {
    set((s) => ({ trucks: { ...s.trucks, loading: true, error: null } }));
    try {
      const data = await API.get("/parade/my-trucks");
      set({ trucks: { data, loading: false, error: null } });
      return data;
    } catch (error) {
      console.error("Failed to fetch my trucks", error);
      set({ trucks: { data: null, loading: false, error: error?.message || "Error fetching trucks" } });
      throw error;
    }
  },

  createTruck: async (payload) => {
    set({ deleteTruckState: { loading: true, error: null } });
    try {
      const result = await API.post("/parade/trucks", payload);
      set({ deleteTruckState: { loading: false, error: null } });
      return result;
    } catch (error) {
      console.error("Failed to create truck", error);
      set({ deleteTruckState: { loading: false, error: error?.message || "Error creating truck" } });
      throw error;
    }
  },

  deleteTruck: async (truckName) => {
    set({ deleteTruckState: { loading: true, error: null } });
    try {
      const result = await API.delete(`/parade/trucks/${encodeURIComponent(truckName)}`);
      set({ deleteTruckState: { loading: false, error: null } });
      return result;
    } catch (error) {
      console.error("Failed to delete truck", error);
      set({ deleteTruckState: { loading: false, error: error?.message || "Error deleting truck" } });
      throw error;
    }
  },

  updateTruck: async (truckName, payload) => {
    set({ updateTruckState: { loading: true, error: null } });
    try {
      const result = await API.put(`/parade/trucks/${encodeURIComponent(truckName)}`, payload);
      set({ updateTruckState: { loading: false, error: null } });
      return result;
    } catch (error) {
      console.error("Failed to update truck", error);
      set({ updateTruckState: { loading: false, error: error?.message || "Error updating truck" } });
      throw error;
    }
  },

  // === COMPANY TRUCKS ===
  // Every truck in the caller's company, addressed by numeric truckId (truck_name is only
  // unique per user, so company-wide edit/delete must go by id).
  fetchCompanyTrucks: async () => {
    set((s) => ({ companyTrucks: { ...s.companyTrucks, loading: true, error: null } }));
    try {
      const data = await API.get("/parade/company-trucks");
      set({ companyTrucks: { data, loading: false, error: null } });
      return data;
    } catch (error) {
      console.error("Failed to fetch company trucks", error);
      set({ companyTrucks: { data: null, loading: false, error: error?.message || "Error fetching company trucks" } });
      throw error;
    }
  },

  updateCompanyTruck: async (truckId, payload) => {
    set({ updateTruckState: { loading: true, error: null } });
    try {
      const result = await API.put(`/parade/company-trucks/${truckId}`, payload);
      set({ updateTruckState: { loading: false, error: null } });
      return result;
    } catch (error) {
      console.error("Failed to update company truck", error);
      set({ updateTruckState: { loading: false, error: error?.message || "Error updating truck" } });
      throw error;
    }
  },

  deleteCompanyTruck: async (truckId) => {
    set({ deleteTruckState: { loading: true, error: null } });
    try {
      const result = await API.delete(`/parade/company-trucks/${truckId}`);
      set({ deleteTruckState: { loading: false, error: null } });
      return result;
    } catch (error) {
      console.error("Failed to delete company truck", error);
      set({ deleteTruckState: { loading: false, error: error?.message || "Error deleting truck" } });
      throw error;
    }
  },

  // === TRUCK FILTERS ===
  fetchTruckFilters: async () => {
    set((s) => ({ truckFilters: { ...s.truckFilters, loading: true, error: null } }));
    try {
      const data = await API.get("/parade/posted-trucks");
      set({ truckFilters: { data, loading: false, error: null } });
      return data;
    } catch (error) {
      console.error("Failed to fetch truck filters", error);
      set({ truckFilters: { data: null, loading: false, error: error?.message || "Error" } });
      throw error;
    }
  },

  // === DAILY CHECK-IN ===
  fetchDailyCheckInStatus: async () => {
    set((s) => ({ dailyCheckIn: { ...s.dailyCheckIn, loading: true, error: null } }));
    try {
      const data = await API.get("/user/daily-check-in");
      set((s) => ({
        dailyCheckIn: { ...s.dailyCheckIn, lastCheckInDate: data?.lastCheckInDate ?? null, fetched: true, loading: false, error: null },
      }));
      return data;
    } catch (error) {
      console.error("Failed to fetch daily check-in status", error);
      set((s) => ({
        dailyCheckIn: { ...s.dailyCheckIn, loading: false, error: error?.message || "Error fetching daily check-in" },
      }));
      throw error;
    }
  },

  completeDailyCheckIn: async (localDate) => {
    set((s) => ({ dailyCheckIn: { ...s.dailyCheckIn, loading: true, error: null } }));
    try {
      await API.post("/user/daily-check-in", { date: localDate });
      set((s) => ({
        dailyCheckIn: { ...s.dailyCheckIn, lastCheckInDate: localDate, loading: false, error: null },
      }));
    } catch (error) {
      console.error("Failed to complete daily check-in", error);
      set((s) => ({
        dailyCheckIn: { ...s.dailyCheckIn, loading: false, error: error?.message || "Error saving daily check-in" },
      }));
      throw error;
    }
  },

  createTruckFilter: async (payload) => {
    set({ createTruckFilterState: { loading: true, error: null } });
    try {
      const result = await API.post("/parade/posted-trucks", payload);
      set({ createTruckFilterState: { loading: false, error: null } });
      return result;
    } catch (error) {
      console.error("Failed to create truck filter", error);
      set({ createTruckFilterState: { loading: false, error: error?.message || "Error creating truck filter" } });
      throw error;
    }
  },

  deleteTruckFilter: async (truckId) => {
    set({ deleteTruckFilterState: { loading: true, error: null } });
    try {
      const result = await API.delete(`/parade/posted-trucks/${truckId}`);
      set({ deleteTruckFilterState: { loading: false, error: null } });
      return result;
    } catch (error) {
      console.error("Failed to delete truck filter", error);
      set({ deleteTruckFilterState: { loading: false, error: error?.message || "Error" } });
      throw error;
    }
  },

  updateTruckFilter: async (filterId, payload) => {
    set({ updateTruckFilterState: { loading: true, error: null } });
    try {
      const result = await API.put(`/parade/posted-trucks/${filterId}`, payload);
      set({ updateTruckFilterState: { loading: false, error: null } });
      return result;
    } catch (error) {
      console.error("Failed to update truck filter", error);
      set({ updateTruckFilterState: { loading: false, error: error?.message || "Error" } });
      throw error;
    }
  },

  // === TRUCK FILTER LOADS ===
  ingestTruckFilterLoads: (newData, options = {}) => {
    const { skipNotifications = false } = options;
    if (!newData || typeof newData !== "object") return {};

    const now = Date.now();
    const newLoadsByFilter = {};
    const previousSeenIds = get().seenLoadIds;

    const changes = [];
    Object.keys(newData).forEach((filterKey) => {
      const seenIds = previousSeenIds[filterKey];
      if (seenIds === undefined) return;
      const seenIdsSet = new Set(seenIds);
      const newIds = (newData[filterKey] || []).map((item) => (item.loadBoardItem || item).uniqueId).filter(Boolean);
      const newIdsSet = new Set(newIds);
      const added = newIds.filter((id) => !seenIdsSet.has(id)).length;
      const removed = seenIds.filter((id) => !newIdsSet.has(id)).length;
      if (added > 0 || removed > 0) changes.push({ filterKey, added, removed });
    });
    if (changes.length > 0) {
      const totalAdded = changes.reduce((sum, c) => sum + c.added, 0);
      const totalRemoved = changes.reduce((sum, c) => sum + c.removed, 0);
      console.log(`[LoadFinder] Refresh detected changes: +${totalAdded} added, -${totalRemoved} removed`, changes);
    } else {
      console.log("[LoadFinder] Refresh completed: no changes");
    }

    set((s) => {
      const newUnreadLoads = { ...s.unreadLoads };
      const newTimestamps = { ...s.newLoadTimestamps };
      const newSeenLoadIds = { ...s.seenLoadIds };

      Object.keys(newData).forEach((filterKey) => {
        const loads = newData[filterKey] || [];

        if (!skipNotifications && !s.notificationsDisabled[filterKey]) {
          const seenIds = s.seenLoadIds[filterKey];
          const isInitialView = seenIds === undefined;

          if (!isInitialView) {
            const seenIdsSet = new Set(seenIds);
            const newUnreadIds = [];
            const newLoads = [];

            loads.forEach((item) => {
              const load = item.loadBoardItem || item;
              const loadId = load.uniqueId;
              if (loadId && !seenIdsSet.has(loadId)) {
                newUnreadIds.push(loadId);
                newLoads.push(item);
                newTimestamps[loadId] = now;
              }
            });

            if (newUnreadIds.length > 0) {
              newLoadsByFilter[filterKey] = newLoads;
              const existing = newUnreadLoads[filterKey] || [];
              newUnreadLoads[filterKey] = [...new Set([...existing, ...newUnreadIds])];
            }
          }
        }

        newSeenLoadIds[filterKey] = loads.map((item) => (item.loadBoardItem || item).uniqueId).filter(Boolean);
      });

      return {
        unreadLoads: newUnreadLoads,
        newLoadTimestamps: newTimestamps,
        seenLoadIds: newSeenLoadIds,
      };
    });

    return newLoadsByFilter;
  },

  // === NOTIFICATION ACTIONS ===
  // Refresh timestamps for unread loads when user clicks on a filter
  // This ensures green highlighting even if original timestamps expired
  refreshNewLoadTimestamps: (filterKey) => {
    set((s) => {
      const unreadIds = s.unreadLoads[filterKey];
      if (!unreadIds || unreadIds.length === 0) return s;

      const now = Date.now();
      const newTimestamps = { ...s.newLoadTimestamps };
      unreadIds.forEach((loadId) => {
        newTimestamps[loadId] = now;
      });
      return { newLoadTimestamps: newTimestamps };
    });
  },

  markFilterLoadsAsRead: (filterKey) => {
    set((s) => {
      const newUnreadLoads = { ...s.unreadLoads };
      delete newUnreadLoads[filterKey];
      return { unreadLoads: newUnreadLoads };
    });
  },

  toggleFilterNotifications: (filterKey) => {
    set((s) => ({
      notificationsDisabled: {
        ...s.notificationsDisabled,
        [filterKey]: !s.notificationsDisabled[filterKey],
      },
    }));
  },

  // Clear the green highlight for a single load (when the user clicks on it).
  // New-load highlights are intentionally NOT time-based — they persist until the
  // user interacts with the load or switches truck/filter.
  clearNewLoadHighlight: (loadId) => {
    set((s) => {
      if (!loadId || s.newLoadTimestamps[loadId] == null) return s;
      const newTimestamps = { ...s.newLoadTimestamps };
      delete newTimestamps[loadId];
      return { newLoadTimestamps: newTimestamps };
    });
  },

  // Clear all green highlights at once (when the user switches truck or filter).
  clearAllNewLoadHighlights: () => {
    set((s) => (Object.keys(s.newLoadTimestamps).length === 0 ? s : { newLoadTimestamps: {} }));
  },

  // === STRATEGY ===
  fetchGlobalStrategyText: async () => {
    set((s) => ({ strategy: { ...s.strategy, fetchTextState: { loading: true, error: null } } }));
    try {
      const data = await fetchAllowing404("/user/strategy/global/text");
      set((s) => ({
        strategy: {
          ...s.strategy,
          globalText: data?.strategyText ?? null,
          fetchTextState: { loading: false, error: null },
        },
      }));
    } catch (error) {
      console.error("Failed to fetch global strategy text", error);
      set((s) => ({
        strategy: { ...s.strategy, fetchTextState: { loading: false, error: error?.message || "Error" } },
      }));
    }
  },

  fetchTruckStrategyText: async (truckId) => {
    set((s) => ({ strategy: { ...s.strategy, fetchTextState: { loading: true, error: null } } }));
    try {
      const data = await fetchAllowing404("/user/strategy/truck/text", { params: { truckId } });
      set((s) => ({
        strategy: {
          ...s.strategy,
          truckText: { ...s.strategy.truckText, [truckId]: data?.strategyText ?? null },
          fetchTextState: { loading: false, error: null },
        },
      }));
    } catch (error) {
      console.error("Failed to fetch truck strategy text", error);
      set((s) => ({
        strategy: { ...s.strategy, fetchTextState: { loading: false, error: error?.message || "Error" } },
      }));
    }
  },

  fetchGlobalPrefill: async (truckId, { force = false } = {}) => {
    if (truckId == null) return;
    if (!force && get().strategy.globalPrefillLoaded[truckId]) return;
    set((s) => ({ strategy: { ...s.strategy, fetchPrefillState: { loading: true, error: null } } }));
    try {
      const data = await fetchAllowing404("/user/strategy/global/prefill", { params: { truckId } });
      set((s) => ({
        strategy: {
          ...s.strategy,
          globalPrefill: { ...s.strategy.globalPrefill, [truckId]: data ?? null },
          globalPrefillLoaded: { ...s.strategy.globalPrefillLoaded, [truckId]: true },
          fetchPrefillState: { loading: false, error: null },
        },
      }));
    } catch (error) {
      console.error("Failed to fetch global strategy prefill", error);
      set((s) => ({
        strategy: { ...s.strategy, fetchPrefillState: { loading: false, error: error?.message || "Error" } },
      }));
    }
  },

  fetchTruckPrefill: async (truckId, { force = false } = {}) => {
    if (!force && get().strategy.truckPrefillLoaded[truckId]) return;
    set((s) => ({ strategy: { ...s.strategy, fetchPrefillState: { loading: true, error: null } } }));
    try {
      const data = await fetchAllowing404("/user/strategy/truck/prefill", { params: { truckId } });
      set((s) => {
        const nextPrefill = { ...s.strategy.truckPrefill };
        if (data) {
          nextPrefill[truckId] = data;
        } else {
          delete nextPrefill[truckId];
        }
        return {
          strategy: {
            ...s.strategy,
            truckPrefill: nextPrefill,
            truckPrefillLoaded: { ...s.strategy.truckPrefillLoaded, [truckId]: true },
            fetchPrefillState: { loading: false, error: null },
          },
        };
      });
    } catch (error) {
      console.error("Failed to fetch truck strategy prefill", error);
      set((s) => ({
        strategy: { ...s.strategy, fetchPrefillState: { loading: false, error: error?.message || "Error" } },
      }));
    }
  },

  // Live preview while typing in the Adjust-strategy modal. Each scope is extracted
  // and stored separately so the preview can render the per-scope source (outlined
  // for global, filled for truck) instead of a merged blob.
  extractStrategyPreview: async ({ truckText, globalText, truckId }) => {
    if (!truckText && !globalText) {
      set((s) => ({
        strategy: {
          ...s.strategy,
          extractPreviewGlobal: null,
          extractPreviewTruck: null,
          extractState: { loading: false, error: null },
        },
      }));
      return;
    }
    set((s) => ({ strategy: { ...s.strategy, extractState: { loading: true, error: null } } }));
    try {
      const extractBody = (strategyText) => (truckId != null ? { strategyText, truckId } : { strategyText });
      const [globalPrefill, truckPrefill] = await Promise.all([
        globalText ? API.post("/user/strategy/extract", extractBody(globalText)) : Promise.resolve(null),
        truckText ? API.post("/user/strategy/extract", extractBody(truckText)) : Promise.resolve(null),
      ]);
      set((s) => ({
        strategy: {
          ...s.strategy,
          extractPreviewGlobal: globalPrefill ?? null,
          extractPreviewTruck: truckPrefill ?? null,
          extractState: { loading: false, error: null },
        },
      }));
    } catch (error) {
      console.error("Failed to extract strategy preview", error);
      set((s) => ({
        strategy: {
          ...s.strategy,
          extractState: { loading: false, error: formatApiError(error, "Failed to extract parameters") },
        },
      }));
    }
  },

  clearExtractPreview: () =>
    set((s) => ({
      strategy: {
        ...s.strategy,
        extractPreviewGlobal: null,
        extractPreviewTruck: null,
        extractState: { loading: false, error: null },
      },
    })),

  saveStrategy: async ({ truckId, globalText, truckText }) => {
    set((s) => ({ strategy: { ...s.strategy, saveState: { loading: true, error: null } } }));
    try {
      const calls = [];
      if (globalText !== null && globalText !== undefined) {
        const globalBody = truckId != null ? { strategyText: globalText, truckId } : { strategyText: globalText };
        calls.push(API.post("/user/strategy/global", globalBody));
      }
      if (truckId != null && truckText !== null && truckText !== undefined) {
        calls.push(API.post("/user/strategy/truck", { truckId, strategyText: truckText }));
      }
      await Promise.all(calls);

      set((s) => ({
        strategy: { ...s.strategy, globalPrefill: {}, globalPrefillLoaded: {} },
      }));

      // Refresh saved text and raw prefills (both scopes) so derived state stays consistent.
      const refreshes = [get().fetchGlobalStrategyText()];
      if (truckId != null) {
        refreshes.push(get().fetchGlobalPrefill(truckId, { force: true }));
        refreshes.push(get().fetchTruckStrategyText(truckId));
        refreshes.push(get().fetchTruckPrefill(truckId, { force: true }));
      }
      await Promise.all(refreshes);

      set((s) => ({ strategy: { ...s.strategy, saveState: { loading: false, error: null } } }));
    } catch (error) {
      console.error("Failed to save strategy", error);
      set((s) => ({
        strategy: {
          ...s.strategy,
          saveState: { loading: false, error: formatApiError(error, "Failed to save strategy") },
        },
      }));
    }
  },

  // === CLEAR ACTIONS ===
  clearLoadFinderState: () => set(initialState),
  clearDeleteFilterError: () => set({ deleteTruckFilterState: { loading: false, error: null } }),
}));

// Selectors for convenience
export const selectTrucks = (state) => state.trucks;
export const selectTruckFilters = (state) => state.truckFilters;
export const selectUnreadLoads = (state) => state.unreadLoads;
export const selectNewLoadTimestamps = (state) => state.newLoadTimestamps;
export const selectNotificationsDisabled = (state) => state.notificationsDisabled;
export const selectStrategy = (state) => state.strategy;
